import { useCallback, useState, FC, useMemo } from 'react'
import { useUpdateGroupMutation } from 'src/app/appApiQueries'
import { setType } from 'src/app/modal'
import { useAppDispatch, useAppSelector } from 'src/hooks'
import ModalTitle from '../ModalTitle'
import ModalButtons from '../ModalButtons'
import useModalLoading from 'src/hooks/useModalLoading'
import { selectGroupName, selectGroupNames } from 'src/app/hostSettings'
import Input from 'src/components/Input'
import { nameOnObject } from 'src/utils/helpers'

type GroupRenameModalProps = {
  groupId: string
}

export const GroupRenameModal: FC<GroupRenameModalProps> = ({ groupId }) => {
  const groupNames = useAppSelector(selectGroupNames)
  // we must memoize re-selectors if they will appear in different component instances
  const selectGroupNameMemo = useMemo(() => selectGroupName, [])
  const groupName = useAppSelector((state) =>
    selectGroupNameMemo(state, groupId)
  )
  const [renameView, setRenameView] = useState<'input' | 'confirm'>('input')
  const [value, setValue] = useState('')
  const [error, setError] = useState('')
  const dispatch = useAppDispatch()
  const [updateGroupTrigger, { isLoading, isSuccess, isError }] =
    useUpdateGroupMutation()

  useModalLoading({
    isLoading,
    isSuccess,
    isError,
  })

  const updateGroup = useCallback(
    ({
      newGroupId,
      newGroupName,
    }: {
      newGroupId?: string
      newGroupName?: string
    }) => {
      updateGroupTrigger({ oldGroupId: groupId, newGroupName, newGroupId })
        .unwrap()
        .then(() => {
          dispatch(setType({ type: null }))
        })
    },
    [dispatch, groupId, updateGroupTrigger]
  )

  const onRename = useCallback(() => {
    if (!nameOnObject(value, groupNames)) {
      updateGroup({ newGroupName: value })
    } else {
      // show confirm modal
      setRenameView('confirm')
    }
  }, [groupNames, updateGroup, value])

  const onCancel = () => {
    dispatch(setType({ type: null }))
  }
  const onDontUse = () => {
    setRenameView('input')
  }

  const onUpdateAnyway = () => {
    const properName = nameOnObject(value, groupNames)

    if (properName) {
      const newGroupId = groupNames[properName]
      updateGroup({ newGroupId })
    }
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    // clear an error first if we have one
    if (error) setError('')
    if (e.target.value.length > 30) {
      setError('Must be less than 30 characters')
    }
    if (e.target.value === groupName) {
      setError('Name is same as previous')
    }
  }

  const disabled = !value || !!error

  return renameView === 'input' ? (
    <>
      <ModalTitle text="Rename Tag" />

      <p aria-label="group-name" className="text-sm">
          Tag
      </p>
      <Input
        labeledBy="group-name"
        value={value}
        onChange={onChange}
        error={error}
      />

      <ModalButtons
        firstButton={{
          name: 'Cancel',
          type: 'secondary',
          onClick: onCancel,
          htmlButtonType: 'button',
        }}
        secondButton={{
          name: 'Rename',
          type: 'primary',
          onClick: onRename,
          disabled,
          htmlButtonType: 'button',
        }}
      />
    </>
  ) : (
    <>
      <h2 className="text-base font-bold leading-6 mb-2">
        "{value}" already exists
      </h2>

      <p className="text-base ">
        If used, all "{groupName}" tags will be changed to “{value}” instead
      </p>

      <ModalButtons
        firstButton={{
          name: 'Use Anyway',
          type: 'secondary',
          onClick: onUpdateAnyway,
          htmlButtonType: 'button',
        }}
        secondButton={{
          name: "Don't Use",
          type: 'primary',
          onClick: onDontUse,
          disabled,
          htmlButtonType: 'button',
        }}
      />
    </>
  )
}
